import { xsalsa20poly1305 } from '@noble/ciphers/salsa.js';

import { CouldNotDecryptError } from '../errors';
import { SALT_SYMMETRIC } from '../salts';
import { Brand } from '../types';
import { loadArrayOrBase64, ArrayOrBase64, deriveSecretFromSeed, randomBytes } from '../utils';

import { hashData } from './hashing';

export type SymEncryptionKey = Brand<Uint8Array<ArrayBuffer>, 'SymEncryptionKey'>;
export type SymEncryptedBlob = Brand<Uint8Array<ArrayBuffer>, 'SymEncryptedBlob'>;

const KEY_LENGTH = 32;
const NONCE_LENGTH = 24;

export function loadSymEncryptedBlob(data: ArrayOrBase64): SymEncryptedBlob {
  return loadArrayOrBase64(data) as SymEncryptedBlob;
}

export function loadSymEncryptionKey(data: ArrayOrBase64): SymEncryptionKey {
  return loadArrayOrBase64(data, KEY_LENGTH) as SymEncryptionKey;
}

export async function deriveSymEncryptionKey(
  seed: Uint8Array<ArrayBuffer>,
  secondarySeed: Uint8Array<ArrayBuffer> | null,
  salt: string,
): Promise<SymEncryptionKey> {
  return loadSymEncryptionKey(
    await deriveSecretFromSeed(seed, secondarySeed, SALT_SYMMETRIC + salt, KEY_LENGTH),
  );
}

export function newSymmetricSecret(): SymEncryptionKey {
  return randomBytes(KEY_LENGTH) as SymEncryptionKey;
}

export function symEncrypt(symKey: SymEncryptionKey, data: Uint8Array): SymEncryptedBlob {
  const nonce = randomBytes(NONCE_LENGTH);
  const box = xsalsa20poly1305(symKey, nonce).encrypt(data);

  const fullMessage = new Uint8Array(nonce.length + box.length);
  fullMessage.set(nonce);
  fullMessage.set(box, nonce.length);

  return loadSymEncryptedBlob(fullMessage);
}

export function symDecrypt(symKey: SymEncryptionKey, data: SymEncryptedBlob): Uint8Array {
  const nonce = data.slice(0, NONCE_LENGTH);
  const message = data.slice(NONCE_LENGTH);

  try {
    return xsalsa20poly1305(symKey, nonce).decrypt(message);
  } catch {
    throw new CouldNotDecryptError();
  }
}

export async function symKeyToSas(symEncryptionKey: SymEncryptionKey): Promise<string> {
  const keyHash = await hashData(symEncryptionKey);
  // TODO: implement this properly
  return keyHash.slice(0, 4).reduce((sas, value) => sas.concat((value % 10).toString()), '');
}
